import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Check } from "lucide-react";
import { getConsent, grantConsent, denyConsent } from "../lib/analytics";

/**
 * CookieBanner — bannière minimale RGPD (Accepter/Refuser + lien vers la
 * politique). Affichée tant que le visiteur n'a pas exprimé un choix.
 *
 * Le tracking GA4 est bloqué par défaut (Consent Mode v2 denied) — la
 * bannière ne fait que mettre à jour le consentement.
 */
export default function CookieBanner() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip the banner on B2B SaaS surfaces that require explicit auth (no analytics there).
    const path = window.location.pathname || "";
    if (path.startsWith("/data-room") || path.startsWith("/admin") || path.startsWith("/mssante/espace")) {
      return;
    }
    setOpen(getConsent() === null);
  }, []);

  if (!open) return null;

  const accept = () => { grantConsent(); setOpen(false); };
  const refuse = () => { denyConsent(); setOpen(false); };

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-1.5rem)] max-w-3xl"
         data-testid="cookie-banner" role="dialog" aria-live="polite" aria-label="Préférences cookies">
      <div className="rounded-2xl border border-slate-200 bg-white/98 backdrop-blur-md shadow-2xl
                      p-4 sm:p-5 flex items-start gap-4 flex-wrap">
        <div className="grid place-items-center h-10 w-10 rounded-full bg-sky-50 text-[#0EA5E9] shrink-0">
          <Cookie size={18} />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="text-[#0F2B5B] font-semibold text-[14px]">Vos préférences cookies</div>
          <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">
            Nous utilisons des cookies de mesure d'audience anonymisée (Google Analytics) pour améliorer votre expérience.
            Aucun cookie publicitaire n'est utilisé. Vous pouvez accepter ou refuser à tout moment.
            <Link to="/donnees" className="ml-1 text-[#0EA5E9] hover:underline">En savoir plus</Link>.
          </p>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <button onClick={refuse} data-testid="cookie-refuse"
                  className="btn-ghost px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5">
            <X size={12} /> Refuser
          </button>
          <button onClick={accept} data-testid="cookie-accept"
                  className="btn-primary px-4 py-2 rounded-full text-[12px] font-medium inline-flex items-center gap-1.5">
            <Check size={12} /> Accepter
          </button>
        </div>
      </div>
    </div>
  );
}
